import jsPDF from "jspdf";
import { computeCnc, buildPanels, formatTRY, SHEET_W, SHEET_H } from "@/lib/pricing";

// jsPDF's default font can't render some Turkish glyphs (ş, ğ, ı, İ) reliably,
// so transliterate to ASCII for consistent output.
function tr(s) {
  if (s == null) return "";
  return String(s)
    .replace(/ş/g, "s").replace(/Ş/g, "S")
    .replace(/ğ/g, "g").replace(/Ğ/g, "G")
    .replace(/ı/g, "i").replace(/İ/g, "I")
    .replace(/ç/g, "c").replace(/Ç/g, "C")
    .replace(/ö/g, "o").replace(/Ö/g, "O")
    .replace(/ü/g, "u").replace(/Ü/g, "U");
}

function hexToRgb(hex) {
  const h = hex.replace("#", "");
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

function money(n) {
  return tr(formatTRY(n)).replace("TRY", "TL").trim();
}

// Marka logosu (beyaz T, bronz başlık bandı üzerinde) — bir kez yüklenir
let _logoCache;
async function loadLogoData() {
  if (_logoCache !== undefined) return _logoCache;
  try {
    const res = await fetch("/logo-t-white.png");
    const blob = await res.blob();
    _logoCache = await new Promise((resolve) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.readAsDataURL(blob);
    });
  } catch {
    _logoCache = null;
  }
  return _logoCache;
}

export async function generateQuotePdf({ config, cost, cnc, orderNumber }) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const W = 210;
  const M = 16;
  let y = 18;

  // Header band (marka logosu + isim)
  const logo = await loadLogoData();
  doc.setFillColor(176, 125, 72);
  doc.rect(0, 0, W, 26, "F");
  if (logo) doc.addImage(logo, "PNG", M, 4, 18, 18);
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.text("Taban Mobilya Atolyesi", M + 22, 16);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text("Ozel Mobilya Teklifi / Quote", W - M, 16, { align: "right" });

  y = 38;
  doc.setTextColor(28, 25, 23);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text("TEKLIF", M, y);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(120, 113, 108);
  const dateStr = new Date().toLocaleString("tr-TR");
  doc.text(`Tarih: ${dateStr}`, W - M, y - 4, { align: "right" });
  if (orderNumber) doc.text(`Siparis No: ${tr(orderNumber)}`, W - M, y, { align: "right" });

  // Specs box
  y += 8;
  doc.setDrawColor(230, 223, 213);
  doc.setFillColor(245, 240, 235);
  doc.roundedRect(M, y, W - 2 * M, 26, 2, 2, "F");
  doc.setTextColor(28, 25, 23);
  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.text("Urun Ozellikleri", M + 4, y + 7);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(
    `Olculer: ${config.width} x ${config.height} x ${config.depth} cm (G x Y x D)`,
    M + 4,
    y + 14
  );
  doc.text(
    `Raf: ${config.shelves} adet   |   Kapak: ${config.doorless ? "yok" : config.doorStyle === "flap" ? config.doors + " adet (duser kapak)" : config.doorLayout === "split3" ? config.doors * 3 + " parca (ust+orta+alt)" : config.doorLayout === "split" ? config.doors * 2 + " parca (ust+alt)" : config.doors + " adet"}`,
    M + 4,
    y + 20
  );
  doc.text(
    `Govde: ${tr(config.bodyMaterialLabel || "")} / ${tr(config.bodyColorLabel)}   |   Kapak: ${tr(config.doorMaterialLabel || "")} / ${tr(config.doorColorLabel)}`,
    W / 2 + 6,
    y + 20
  );

  // Cost table
  y += 34;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("Maliyet Dokumu", M, y);
  y += 4;
  doc.setDrawColor(230, 223, 213);
  doc.line(M, y, W - M, y);
  y += 6;
  doc.setFontSize(9);
  cost.items.filter((it) => !it.hidden).forEach((it) => {
    doc.setFont("helvetica", "bold");
    doc.setTextColor(28, 25, 23);
    doc.text(tr(it.label), M, y);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(120, 113, 108);
    if (it.detail) doc.text(tr(it.detail), M + 60, y);
    doc.setTextColor(28, 25, 23);
    doc.text(money(it.amount), W - M, y, { align: "right" });
    y += 6;
  });
  doc.setDrawColor(176, 125, 72);
  doc.line(M, y, W - M, y);
  y += 8;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.setTextColor(176, 125, 72);
  doc.text("TOPLAM", M, y);
  doc.text(money(cost.total), W - M, y, { align: "right" });
  doc.setTextColor(120, 113, 108);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  y += 5;
  doc.text("KDV dahil - malzeme + iscilik + montaj", M, y);

  // CNC metrics
  y += 12;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(28, 25, 23);
  doc.text("CNC Kesim Plani (2.10m x 2.80m tabaka)", M, y);
  y += 6;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(120, 113, 108);
  doc.text(
    `Tabaka: ${cnc.sheetCount} adet   |   Fire: %${cnc.firePercent}   |   Kullanilan: ${cnc.usedAreaM2} m2   |   Parca: ${cnc.partCount}`,
    M,
    y
  );

  // Draw first sheet nesting (using that sheet's own dimensions)
  y += 6;
  const full = computeCnc(config);
  const sheet0 = full.sheets[0] || { w: SHEET_W, h: SHEET_H, placements: [] };
  const placements = sheet0.placements || [];
  const drawW = W - 2 * M;
  const scale = drawW / sheet0.w;
  const drawH = sheet0.h * scale;
  doc.setFillColor(38, 38, 38);
  doc.rect(M, y, drawW, drawH, "F");
  placements.forEach((p) => {
    const [r, g, b] = hexToRgb(p.color);
    doc.setFillColor(r, g, b);
    doc.setDrawColor(253, 251, 247);
    doc.rect(M + p.x * scale, y + p.y * scale, p.w * scale, p.h * scale, "FD");
  });
  if (full.sheetCount > 1) {
    doc.setTextColor(120, 113, 108);
    doc.setFontSize(8);
    doc.text(`(+${full.sheetCount - 1} ek tabaka)`, W - M, y + drawH + 4, { align: "right" });
  }

  // Footer
  doc.setTextColor(168, 162, 158);
  doc.setFontSize(8);
  doc.text(
    "Taban Mobilya Atolyesi - Bu teklif bilgilendirme amaclidir.",
    M,
    287
  );

  const fname = orderNumber ? `teklif-${orderNumber}.pdf` : `taban-mobilya-teklif.pdf`;
  doc.save(fname);
}

// ---------------------------------------------------------------------------
// CNC Üretim Fişi (atölye/üretim için) — malzeme listesi + tam kesim listesi +
// tabaka yerleşim planları + cam/ayna ölçüleri. Admin siparişten çıktı alır.
// ---------------------------------------------------------------------------
export async function generateCncProductionPdf({ config, cost, cnc, orderNumber, imageData }) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const W = 210;
  const H = 297;
  const M = 14;
  const BOTTOM = 282;

  // Hesaplar config'ten yeniden türetilir (net ve hatasız)
  const plan = computeCnc(config);
  const panels = buildPanels(config);
  const cutPanels = panels.filter((p) => !p.nocut);

  let page = 1;
  const groupLabel = (g) => (g === "door" ? "KAPAK" : "GOVDE");

  const footer = () => {
    doc.setTextColor(168, 162, 158);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.text("Taban Mobilya Atolyesi - CNC Uretim Fisi", M, 290);
    doc.text(`Sayfa ${page}`, W - M, 290, { align: "right" });
  };

  const header = (subtitle) => {
    doc.setFillColor(28, 25, 23);
    doc.rect(0, 0, W, 22, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text("CNC URETIM FISI", M, 14);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(214, 211, 209);
    doc.text(tr(subtitle || ""), W - M, 14, { align: "right" });
    doc.setTextColor(28, 25, 23);
  };

  const newPage = (subtitle) => {
    footer();
    doc.addPage();
    page += 1;
    header(subtitle);
  };

  const logo = await loadLogoData();
  header(orderNumber ? `Siparis ${orderNumber}` : new Date().toLocaleDateString("tr-TR"));
  if (logo) doc.addImage(logo, "PNG", W - M - 16, 3, 16, 16);

  let y = 30;

  // --- Sipariş & ürün bilgisi ---
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("URUN BILGISI", M, y);
  y += 2;
  doc.setDrawColor(230, 223, 213);
  doc.line(M, y, W - M, y);
  y += 6;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(40, 36, 34);
  const doorDesc = config.doorless
    ? "Kapaksiz"
    : config.doorStyle === "flap"
    ? `${config.doors} duser kapak${config.flapHandle === "push" ? " (bas-ac)" : ""}`
    : config.doorLayout === "split3"
    ? `${config.doors} x 3 parca kapak`
    : config.doorLayout === "split"
    ? `${config.doors} x 2 parca kapak`
    : `${config.doors} kapak`;
  const infoLines = [
    `Olculer (G x Y x D): ${config.width} x ${config.height} x ${config.depth} cm`,
    `Govde: ${tr(config.bodyMaterialLabel || config.bodyMaterial)} / ${tr(config.bodyColorLabel || config.bodyColor)}`,
    `Kapak: ${config.doorless ? "-" : `${tr(config.doorMaterialLabel || config.doorMaterial)} / ${tr(config.doorColorLabel || config.doorColor || "-")}`}  (${doorDesc})`,
    `Raf: ${config.shelves} adet  |  Orta dikme: ${config.dividers ?? 0}  |  Cekmece: ${(config.drawers ?? 0) * ((config.dividers ?? 0) + 1)}`,
    `Alt bolum: ${config.base === "alt_kapama" ? "Alt kapama (8 cm)" : config.base === "ayak" ? "Ayak (10 cm)" : "Yok"}  |  Arka panel: ${config.noBackPanel && config.doorless ? "Yok" : "Var"}`,
  ];
  infoLines.forEach((l) => {
    doc.text(tr(l), M, y);
    y += 5.5;
  });

  // --- 3D model görüntüsü (tam genişlik, yüksek çözünürlük) ---
  if (imageData) {
    try {
      const imgW = W - 2 * M; // 182 mm
      const imgH = imgW / 2;  // yakalama karesi ~2:1 (1200x600)
      if (y + imgH > BOTTOM - 10) { newPage(orderNumber ? `Siparis ${orderNumber}` : ""); y = 30; }
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.text("3D MODEL GORUNTUSU", M, y);
      y += 3;
      doc.setDrawColor(230, 223, 213);
      doc.setFillColor(41, 37, 36);
      doc.roundedRect(M, y, imgW, imgH, 2, 2, "FD");
      doc.addImage(imageData, "PNG", M + 1.5, y + 1.5, imgW - 3, imgH - 3);
      y += imgH + 6;
    } catch (e) {
      /* görsel eklenemezse fiş yine de üretilir */
    }
  }

  // --- Malzeme & donanım özeti ---
  y += 4;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("MALZEME & DONANIM OZETI", M, y);
  y += 2;
  doc.line(M, y, W - M, y);
  y += 6;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  const matLines = [
    `Tabaka (toplam): ${plan.sheetCount} adet  [Govde: ${plan.bodySheetCount}, Kapak: ${plan.doorSheetCount}]`,
    `Tabaka olcusu: ${plan.sheetW} x ${plan.sheetH} cm${plan.doorSheetW !== plan.sheetW ? `  (kapak tabakasi: ${plan.doorSheetW} x ${plan.doorSheetH} cm)` : ""}`,
    `Kullanilan alan: ${plan.usedAreaM2} m2  |  Fire: %${plan.firePercent}  |  Kesilecek parca: ${plan.partCount} adet`,
    `Kenar bandi (toplam cevre): ${cutPanels.reduce((s, p) => s + (2 * (p.w + p.h)) / 100, 0).toFixed(1)} m`,
  ];
  matLines.forEach((l) => {
    doc.text(tr(l), M, y);
    y += 5.5;
  });
  // Donanım kalemleri (maliyet dökümünden: menteşe/kulp/ray/ayak)
  const hw = (cost?.items || []).filter((it) =>
    ["Menteşe", "Düşer Kapak Mekanizması", "Kulp", "Çekmece Rayı & Aksesuar", "Dolap Ayağı"].includes(it.label)
  );
  if (hw.length) {
    y += 1;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text("Donanim:", M, y);
    doc.setFont("helvetica", "normal");
    let hx = M + 20;
    hw.forEach((it) => {
      const txt = `${tr(it.label)}: ${tr(it.detail || "")}`;
      if (hx > W - M - 50) {
        y += 5;
        hx = M + 20;
      }
      doc.text(txt, hx, y);
      hx += doc.getTextWidth(txt) + 8;
    });
    y += 6;
  }

  // --- Kesim listesi (tam parça dökümü) ---
  // Aynı isim+ölçü parçaları adetle gruplanır
  const map = new Map();
  cutPanels.forEach((p) => {
    const w = +p.w.toFixed(1);
    const h = +p.h.toFixed(1);
    const key = `${p.group}|${p.name}|${w}|${h}`;
    if (map.has(key)) map.get(key).qty += 1;
    else map.set(key, { group: p.group, name: p.name, w, h, qty: 1 });
  });
  const rows = [...map.values()].sort((a, b) =>
    a.group === b.group ? b.w * b.h - a.w * a.h : a.group === "door" ? 1 : -1
  );

  y += 4;
  if (y > BOTTOM - 40) newPage(orderNumber ? `Siparis ${orderNumber}` : ""), (y = 30);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("KESIM LISTESI (NET OLCULER, cm)", M, y);
  y += 4;
  // Tablo başlığı
  const cols = { no: M, grup: M + 12, parca: M + 40, en: M + 112, boy: M + 140, adet: M + 170 };
  doc.setFillColor(245, 240, 235);
  doc.rect(M, y, W - 2 * M, 7, "F");
  doc.setFontSize(8.5);
  doc.setTextColor(60, 54, 50);
  doc.text("#", cols.no + 1, y + 5);
  doc.text("GRUP", cols.grup, y + 5);
  doc.text("PARCA", cols.parca, y + 5);
  doc.text("EN", cols.en, y + 5);
  doc.text("BOY", cols.boy, y + 5);
  doc.text("ADET", cols.adet, y + 5);
  y += 10;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  let idx = 1;
  rows.forEach((r) => {
    if (y > BOTTOM - 6) {
      newPage(orderNumber ? `Siparis ${orderNumber}` : "");
      y = 30;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
    }
    doc.setTextColor(28, 25, 23);
    doc.text(String(idx), cols.no + 1, y);
    doc.setTextColor(120, 113, 108);
    doc.text(groupLabel(r.group), cols.grup, y);
    doc.setTextColor(28, 25, 23);
    doc.text(tr(r.name), cols.parca, y);
    doc.text(r.w.toFixed(1), cols.en, y);
    doc.text(r.h.toFixed(1), cols.boy, y);
    doc.setFont("helvetica", "bold");
    doc.text(`x${r.qty}`, cols.adet, y);
    doc.setFont("helvetica", "normal");
    y += 5.5;
    doc.setDrawColor(240, 234, 226);
    doc.line(M, y - 2, W - M, y - 2);
    idx += 1;
  });

  // --- Tabaka yerleşim planları ---
  plan.sheets.forEach((s, si) => {
    newPage(orderNumber ? `Siparis ${orderNumber}` : "");
    y = 30;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    const gName = s.group === "mixed" ? "Govde & Kapak" : s.group === "door" ? "Kapak" : "Govde";
    doc.text(`TABAKA #${si + 1} - ${tr(gName)}  (${s.w} x ${s.h} cm)`, M, y);
    y += 6;
    const drawW = W - 2 * M;
    const scale = drawW / s.w;
    const drawH = s.h * scale;
    doc.setFillColor(38, 38, 38);
    doc.rect(M, y, drawW, drawH, "F");
    (s.placements || []).forEach((p) => {
      const [r, g, b] = hexToRgb(p.color);
      doc.setFillColor(r, g, b);
      doc.setDrawColor(253, 251, 247);
      doc.setLineWidth(0.3);
      const px = M + p.x * scale;
      const py = y + p.y * scale;
      const pw = p.w * scale;
      const ph = p.h * scale;
      doc.rect(px, py, pw, ph, "FD");
      // Parça etiketi + ölçü (yeterince büyükse)
      if (pw > 22 && ph > 9) {
        doc.setTextColor(20, 18, 16);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(6.5);
        doc.text(tr(p.name), px + pw / 2, py + ph / 2 - 1, { align: "center", maxWidth: pw - 2 });
        doc.setFont("helvetica", "normal");
        doc.setFontSize(6);
        doc.text(`${p.w.toFixed(1)} x ${p.h.toFixed(1)}`, px + pw / 2, py + ph / 2 + 4, { align: "center" });
      }
    });
    y += drawH + 5;
    doc.setTextColor(120, 113, 108);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text(`Parca sayisi: ${(s.placements || []).length}  |  Olculer parca uzerinde yazilidir (en x boy, cm)`, M, y);
  });

  // --- Cam kapak ölçüleri ---
  if (plan.glassPanels && plan.glassPanels.length) {
    if (y > BOTTOM - 40) { newPage(orderNumber ? `Siparis ${orderNumber}` : ""); y = 30; }
    else y += 8;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(28, 25, 23);
    doc.text("CAM KAPAK OLCULERI (tabaka kesimine girmez)", M, y);
    y += 6;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    plan.glassPanels.forEach((g) => {
      doc.text(tr(`${g.name}: kapak ${g.w} x ${g.h} cm  |  cam (cerceve payi dusulmus): ${g.glassW} x ${g.glassH} cm`), M, y);
      y += 5.5;
    });
  }

  // --- Ayna kapak ölçüleri ---
  if (plan.mirrorPanels && plan.mirrorPanels.length) {
    if (y > BOTTOM - 30) { newPage(orderNumber ? `Siparis ${orderNumber}` : ""); y = 30; }
    else y += 6;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(28, 25, 23);
    doc.text("AYNA KAPAK OLCULERI (tabaka kesimine girmez)", M, y);
    y += 6;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    plan.mirrorPanels.forEach((g) => {
      doc.text(tr(`${g.name}: ayna ${g.w} x ${g.h} cm (tam kapak olcusu)`), M, y);
      y += 5.5;
    });
  }

  // --- Yerleşmeyen parça uyarısı ---
  if (plan.unplaced && plan.unplaced.length) {
    if (y > BOTTOM - 30) { newPage(orderNumber ? `Siparis ${orderNumber}` : ""); y = 30; }
    else y += 6;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(185, 28, 28);
    doc.text("DIKKAT - TABAKAYA SIGMAYAN PARCALAR", M, y);
    y += 6;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(28, 25, 23);
    plan.unplaced.forEach((p) => {
      doc.text(tr(`${p.name}: ${p.w} x ${p.h} cm — ayrica degerlendirilmeli`), M, y);
      y += 5.5;
    });
  }

  footer();
  const fname = orderNumber ? `cnc-uretim-${orderNumber}.pdf` : `taban-mobilya-cnc-uretim.pdf`;
  doc.save(fname);
}

// ---------------------------------------------------------------------------
// Kesim listesi CSV çıktısı — panel optimizasyon/kesim yazılımlarına import için.
// ";" ayraçlı (Excel TR uyumlu), UTF-8 BOM'lu, ondalık nokta kullanır.
// ---------------------------------------------------------------------------
export function downloadCutListCsv({ config, orderNumber }) {
  const panels = buildPanels(config).filter((p) => !p.nocut); // cam/ayna kesime girmez
  const map = new Map();
  panels.forEach((p) => {
    const w = +p.w.toFixed(1);
    const h = +p.h.toFixed(1);
    const key = `${p.group}|${p.name}|${w}|${h}`;
    if (map.has(key)) map.get(key).qty += 1;
    else map.set(key, { group: p.group, name: p.name, w, h, qty: 1 });
  });
  const rows = [...map.values()].sort((a, b) =>
    a.group === b.group ? b.w * b.h - a.w * a.h : a.group === "door" ? 1 : -1
  );
  const bodyMat = `${config.bodyMaterialLabel || config.bodyMaterial || ""} ${config.bodyColorLabel || config.bodyColor || ""}`.trim();
  const doorMat = `${config.doorMaterialLabel || config.doorMaterial || ""} ${config.doorColorLabel || config.doorColor || ""}`.trim();

  const lines = [
    "Sipariş;Parça;Grup;En (cm);Boy (cm);Adet;Kenar Bandı (m);Malzeme",
    ...rows.map((r) =>
      [
        orderNumber || "-",
        r.name,
        r.group === "door" ? "Kapak" : "Gövde",
        r.w.toFixed(1),
        r.h.toFixed(1),
        r.qty,
        (((2 * (r.w + r.h)) / 100) * r.qty).toFixed(2),
        r.group === "door" ? doorMat : bodyMat,
      ].join(";")
    ),
  ];
  const blob = new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = orderNumber ? `cnc-kesim-listesi-${orderNumber}.csv` : "cnc-kesim-listesi.csv";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
